import { computed, ref, shallowRef } from 'vue';
import { acceptHMRUpdate, defineStore } from 'pinia';
import { StorageSerializers, useLocalStorage } from '@vueuse/core';
import { cloneDeep } from 'lodash';
import { usePrintingStore } from '@/composition/use-printing';
import { IBridgePrintingStrategy } from '@/composition/printing/printing-strategy-bridge';
import { PrintBridgeError } from '@/lib/print-bridge/print-bridge-client';
import { useUi } from '@/composition/use-ui';
import { AlertType } from '@/interfaces/IAlert';
import { IPrinter } from '@/interfaces/IPrinter';
import { IPrintingSettings } from '@/interfaces/IPrintingSettings';
import { PrintPluginStatus } from '@/modules/PrintService';
import {
  ILogicalPrinter,
  IPrintingV2Config,
  PRINTING_V2_SCHEMA_VERSION,
  createEmptyPrintingV2Config,
  isConfigNonEmpty,
  isPrintingV2Config,
  manualPrinterParamsFromRef,
} from './printing-v2.config';
import {
  IPrinterSettingsEntry,
  normalizeEscposSettings,
  normalizeZplSettings,
} from '@/lib/print/printer-settings';
import {
  IV1PersistedPrintingSettings,
  migrateV1PrintingSettings,
} from './printing-v2.migration';

const LEGACY_V1_STORAGE_KEY = 'printing-settings';

/**
 * V2 printing settings store (bridge mode only).
 *
 * SOURCE OF TRUTH: the BRIDGE's config store — it survives browser-data
 * clearing and is shared by every browser on the PC. The localStorage cache
 * holds the last adopted document verbatim, as a cache only. Scoping is
 * machine-level (matches today's cross-branch localStorage semantics; station
 * ids are globally-unique DB ids).
 */
export const usePrintingV2Store = defineStore('printing-v2', () => {
  const cache = useLocalStorage<IPrintingV2Config | null>(
    'printing-settings-v2',
    null,
    { serializer: StorageSerializers.object }
  );

  const config = ref<IPrintingV2Config | null>(null);
  const revision = ref(0);
  /** Stale-build guard: a newer-schema document disables all editing. */
  const configReadOnly = ref(false);
  const hydrated = ref(false);
  const bridgeStrategy = shallowRef<IBridgePrintingStrategy | null>(null);

  const ui = useUi();

  /**
   * What the UI should render: the adopted document, falling back to the
   * local cache while the bridge is disconnected (grayed-out grid state).
   */
  const displayConfig = computed(() => config.value ?? cache.value);

  const logicalPrinters = computed(() => config.value?.logicalPrinters ?? []);
  const assignments = computed(
    () =>
      config.value?.assignments ?? {
        ticket: null,
        sticker: null,
        comandaByStation: {},
      }
  );
  const unresolvedV1 = computed(() => config.value?.unresolvedV1 ?? []);

  /**
   * Called by the strategy wiring BEFORE the bridge strategy connects: hooks
   * the config hydration into the connect sequence (status flips to connected
   * only after hydration + projection complete) and subscribes to live
   * cross-client config updates.
   */
  function attachBridge(strategy: IBridgePrintingStrategy) {
    bridgeStrategy.value = strategy;
    strategy.setReadyHook(hydrate);
    strategy.onConfigChanged((payload) => {
      adopt(payload.config, payload.revision);
    });
  }

  /** Adopt a document wholesale (no field merging): state + cache + legacy projection. */
  function adopt(docUnknown: unknown, rev: number) {
    let doc: IPrintingV2Config | null = null;
    let malformed = false;
    if (isPrintingV2Config(docUnknown)) {
      doc = docUnknown;
    } else if (docUnknown !== null && docUnknown !== undefined) {
      malformed = true;
      console.error('[PrintingV2] ignoring malformed config document');
    }

    configReadOnly.value =
      !!doc && doc.schemaVersion > PRINTING_V2_SCHEMA_VERSION;
    config.value = doc;
    revision.value = rev;
    // A malformed document must NOT wipe the cache: it is the only good
    // local copy left (disconnected display + bridge-reinstall recovery).
    if (!malformed) cache.value = doc ? cloneDeep(doc) : null;
    projectToLegacy();
    hydrated.value = true;
  }

  /** B3 hydration decision matrix — runs inside the strategy's connect sequence. */
  async function hydrate() {
    const strategy = bridgeStrategy.value;
    if (!strategy) return;

    const res = await strategy.configGet();

    // (2) Bridge has a document.
    if (res.config !== null && res.config !== undefined) {
      const remote = isPrintingV2Config(res.config) ? res.config : null;
      const cached = cache.value;

      // Cache strictly newer (bridge reinstall on this machine; same-machine
      // clock so timestamps are comparable) → push the cache up. A
      // newer-schema cache never writes (stale-build guard).
      const cacheIsNewer =
        remote !== null &&
        cached !== null &&
        isConfigNonEmpty(cached) &&
        cached.schemaVersion <= PRINTING_V2_SCHEMA_VERSION &&
        cached.savedAt > remote.savedAt;

      if (cacheIsNewer && cached) {
        await pushUp(cached, res.revision, { reconcileManual: true });
      } else {
        adopt(res.config, res.revision);
      }
      return;
    }

    // (3) Document null + cache non-empty → push the cache up.
    const cached = cache.value;
    if (cached && isConfigNonEmpty(cached)) {
      await pushUp(cached, res.revision, { reconcileManual: true });
      return;
    }

    // (4) Document null + cache empty → v1 migration (once per machine — the
    // migratedFromV1 flag lives in the bridge document).
    const migrated = migrateV1PrintingSettings({
      legacy: readLegacyV1Settings(),
      printers: strategy.bridgePrinters.value,
      now: Date.now(),
    });

    if (migrated) {
      await pushUp(migrated, res.revision, { reconcileManual: false });
    } else {
      // Empty legacy: write NOTHING (revision stays 0) so a different browser
      // holding real v1 data can still migrate later.
      adopt(null, res.revision);
    }
  }

  async function pushUp(
    doc: IPrintingV2Config,
    baseRevision: number,
    { reconcileManual }: { reconcileManual: boolean }
  ) {
    const strategy = bridgeStrategy.value;
    if (!strategy) return;

    try {
      const ack = await strategy.configSet({ config: doc, baseRevision });
      adopt(doc, ack.revision);
      if (reconcileManual) await reconcileManualRefs(doc, strategy);
    } catch (e) {
      if (e instanceof PrintBridgeError && e.code === 'PB:CONFIG_CONFLICT') {
        // Another client just wrote → re-get and ADOPT (never re-push).
        const fresh = await strategy.configGet();
        adopt(fresh.config, fresh.revision);
        return;
      }

      // Unexpected write failure: adopt the pushed document locally so
      // printing works this session; the stale revision makes the next
      // mutation conflict → re-get, self-healing.
      console.error('[PrintingV2] config push-up failed', e);
      adopt(doc, baseRevision);
    }
  }

  /**
   * After a cache push-up, a bridge reinstall may have wiped its persisted
   * manual (tcp/serial-bt) entries — re-create any the config references so
   * it doesn't point at ghosts.
   */
  async function reconcileManualRefs(
    doc: IPrintingV2Config,
    strategy: IBridgePrintingStrategy
  ) {
    const knownRefs = new Set(strategy.bridgePrinters.value.map((p) => p.ref));

    for (const logical of doc.logicalPrinters) {
      if (logical.transport === 'spooler') continue;
      if (knownRefs.has(logical.ref)) continue;

      const params = manualPrinterParamsFromRef(logical);
      if (!params) continue;

      try {
        await strategy.addManualPrinter(params);
      } catch (e) {
        console.error(
          '[PrintingV2] manual ref reconciliation failed',
          logical.ref,
          e
        );
      }
    }
  }

  function readLegacyV1Settings(): IV1PersistedPrintingSettings | null {
    try {
      const raw = localStorage.getItem(LEGACY_V1_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as unknown;
      if (typeof parsed !== 'object' || parsed === null) return null;
      return parsed as IV1PersistedPrintingSettings;
    } catch (e) {
      console.error('[PrintingV2] could not read legacy v1 settings', e);
      return null;
    }
  }

  // #region mutations

  /** Single-in-flight serialization: rapid edits queue instead of interleaving. */
  let mutationChain: Promise<unknown> = Promise.resolve();

  /**
   * THE one mutation helper used by all writers (wizard finish, printer
   * edit/remove, assignment/trigger changes, unresolved-v1 resolution):
   * pessimistic — only an acked config.set commits to store + cache + legacy
   * projection. Requires a connected bridge; there is NO offline write queue.
   *
   * @returns whether the mutation was committed.
   */
  function mutateConfig(
    mutator: (draft: IPrintingV2Config) => void
  ): Promise<boolean> {
    const run = mutationChain.then(
      () => _mutateConfig(mutator),
      () => _mutateConfig(mutator)
    );
    mutationChain = run;
    return run;
  }

  async function _mutateConfig(
    mutator: (draft: IPrintingV2Config) => void
  ): Promise<boolean> {
    const strategy = bridgeStrategy.value;

    if (
      !strategy ||
      strategy.status.value !== PrintPluginStatus.Connected ||
      !hydrated.value
    ) {
      ui.showToast(
        AlertType.Error,
        'Sin conexión con el plugin de impresión, no se pudo guardar'
      );
      return false;
    }

    if (configReadOnly.value) {
      ui.showToast(
        AlertType.Error,
        'Actualice la página para editar la configuración de impresión'
      );
      return false;
    }

    for (let attempt = 0; attempt < 2; attempt++) {
      const draft = cloneDeep(config.value ?? createBaseConfig());
      mutator(draft);
      draft.savedAt = Date.now();

      try {
        const ack = await strategy.configSet({
          config: draft,
          baseRevision: revision.value,
        });
        adopt(draft, ack.revision);
        return true;
      } catch (e) {
        if (e instanceof PrintBridgeError && e.code === 'PB:CONFIG_CONFLICT') {
          // Another client wrote concurrently: adopt the fresh document,
          // re-apply the mutator over it and retry once. The re-get itself
          // can fail (bridge drop mid-conflict) — that must still resolve
          // to false, never reject out of mutateConfig.
          try {
            const fresh = await strategy.configGet();
            adopt(fresh.config, fresh.revision);
            if (attempt === 0) continue;
          } catch (refreshError) {
            ui.serverError(
              refreshError,
              'No se pudo guardar la configuración de impresión'
            );
            return false;
          }
        }

        ui.serverError(e, 'No se pudo guardar la configuración de impresión');
        return false;
      }
    }

    return false;
  }

  /**
   * Base document for the first mutation on a fresh machine: seeds the
   * destination settings from this browser's current legacy objects so
   * existing triggers aren't lost.
   */
  function createBaseConfig(): IPrintingV2Config {
    const legacyStore = usePrintingStore();
    const base = createEmptyPrintingV2Config();
    base.destinationSettings = {
      ticket: { ...legacyStore.ticketPrintingSettings },
      comanda: { ...legacyStore.comandaPrintingSettings },
      sticker: { ...legacyStore.stickerPrintingSettings },
    };
    return base;
  }

  // #endregion

  // #region legacy projection

  /**
   * Imperative projection of v2 state into the legacy printing store (NOT a
   * Vue watch: hydration must complete synchronously-in-sequence before the
   * strategy flips to connected). Active only in bridge mode. The document
   * pipeline keeps reading the legacy store — zero pipeline changes.
   */
  function projectToLegacy() {
    const legacyStore = usePrintingStore();
    if (legacyStore.bridgeMode !== 'bridge') return;

    const doc = config.value;
    const byId = new Map<number, ILogicalPrinter>(
      (doc?.logicalPrinters ?? []).map((lp) => [lp.id, lp])
    );

    const toIPrinter = (
      logicalId: number | null | undefined
    ): IPrinter | null => {
      const logical = logicalId != null ? byId.get(logicalId) : undefined;
      return logical
        ? { id: logical.ref, name: logical.name, type: 'usb' }
        : null;
    };

    const codepageOf = (
      logicalId: number | null | undefined
    ): string | undefined => {
      const logical = logicalId != null ? byId.get(logicalId) : undefined;
      return logical && logical.format === 'escpos'
        ? logical.codepage
        : undefined;
    };

    const docAssignments = doc?.assignments ?? {
      ticket: null,
      sticker: null,
      comandaByStation: {},
    };

    legacyStore.setTicketPrinter(toIPrinter(docAssignments.ticket));
    legacyStore.setStickerPrinter(toIPrinter(docAssignments.sticker));

    // Union of currently-projected and document stations, so removed
    // assignments clear their runtime printer too.
    const stationIds = new Set<number>([
      ...Object.keys(legacyStore.comandaPrintersByStation).map(Number),
      ...Object.keys(docAssignments.comandaByStation).map(Number),
    ]);
    for (const stationId of stationIds) {
      legacyStore.setComandaPrinter(
        stationId,
        toIPrinter(docAssignments.comandaByStation[stationId])
      );
    }

    // Per-printer hardware settings (win over the settings objects'
    // encoding). Normalized here so renderers read complete, valid shapes.
    // zpl printers WITHOUT explicit zpl settings get no `zpl` entry: the
    // legacy localStorage stickerSettings resolve at PRINT time, preserving
    // pre-existing printers' staleness behavior.
    const settingsByRef: Record<string, IPrinterSettingsEntry> = {};
    for (const logical of doc?.logicalPrinters ?? []) {
      if (logical.format === 'escpos') {
        // Dormant-data guard: paperWidthMm has existed in the config schema
        // since the first wizard, but the old renderer NEVER used it for
        // text layout (everything rendered with the 80mm table) or QR
        // sizing. Activating it on stored configs would silently re-flow
        // every receipt of pre-existing 58mm printers on deploy day. Gate
        // on `model` — written by every new-build save (wizard + edit
        // modal) and by nothing older — so 58mm semantics apply only to
        // configs saved under the new model.
        const savedByNewBuild = logical.model !== undefined;
        settingsByRef[logical.ref] = {
          escpos: normalizeEscposSettings({
            model: logical.model,
            codepage: logical.codepage,
            paperWidthMm: savedByNewBuild ? logical.paperWidthMm : undefined,
            qrMode: logical.qrMode,
            charsPerLineNormal: logical.charsPerLineNormal,
            charsPerLineSmall: logical.charsPerLineSmall,
            cutType: logical.cutType,
            cutMode: logical.cutMode,
            feedLines: logical.feedLines,
          }),
        };
      } else {
        settingsByRef[logical.ref] = logical.zpl
          ? { zpl: normalizeZplSettings(logical.zpl) }
          : {};
      }
    }
    legacyStore.printerSettingsByRef = settingsByRef;

    if (!doc) return;

    // Settings objects: v2 destinationSettings with encoding overridden by
    // the assigned printer's codepage. For comanda (N stations, one legacy
    // object) the lowest-numbered assigned station's codepage is the
    // fallback only — printerSettingsByRef wins per station.
    const lowestAssignedStation = Object.keys(docAssignments.comandaByStation)
      .map(Number)
      .filter((sid) => docAssignments.comandaByStation[sid] != null)
      .sort((a, b) => a - b)[0];

    applySettings(
      legacyStore.ticketPrintingSettings,
      doc.destinationSettings.ticket,
      codepageOf(docAssignments.ticket)
    );
    applySettings(
      legacyStore.comandaPrintingSettings,
      doc.destinationSettings.comanda,
      lowestAssignedStation !== undefined
        ? codepageOf(docAssignments.comandaByStation[lowestAssignedStation])
        : undefined
    );
    applySettings(
      legacyStore.stickerPrintingSettings,
      doc.destinationSettings.sticker,
      codepageOf(docAssignments.sticker)
    );
  }

  function applySettings(
    target: IPrintingSettings,
    source: IPrintingSettings,
    codepageOverride: string | undefined
  ) {
    target.trigger = source.trigger;
    target.source = source.source;
    target.encoding = codepageOverride ?? source.encoding;
    target.escPosFormat = source.escPosFormat;
  }

  // #endregion

  /**
   * Correctly-typed bridge accessor: the strategy object's nested refs stay
   * refs at runtime (markRaw), but Pinia's UnwrapRef store typing would claim
   * otherwise on direct `store.bridgeStrategy` property access.
   */
  function getBridge(): IBridgePrintingStrategy | null {
    return bridgeStrategy.value;
  }

  const actions = {
    attachBridge,
    hydrate,
    mutateConfig,
    projectToLegacy,
    getBridge,
  };

  return {
    config,
    displayConfig,
    revision,
    configReadOnly,
    hydrated,
    bridgeStrategy,
    logicalPrinters,
    assignments,
    unresolvedV1,
    ...actions,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(usePrintingV2Store, import.meta.hot));
}
